import { publicGender, visibleProfileReveals } from '../../util/profileReveals';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import browsePhoto from '../../assets/landing/home-d-video-call.jpg';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import ListenerBrowseCard from '../../components/ListenerBrowseCard/ListenerBrowseCard';
import { useListenerListings } from '../../components/TalkAbout/TalkerMatchRow';
import { skillOffersFromListing, SKILL_CATEGORIES, LISTENING_OFFER_ID, listeningOfferFromListing } from '../../util/sweetSkills';
import { topicsFromListing, SESSION_TOPIC_LABELS } from '../../util/sessionTopics';
import { pathsFromListing, legacyTopicForPath, pathLabels, LEGACY_TO_PATH, childrenOf, aliasOf } from '../../util/topicPaths';
import { usePublicOffers, listenerOffersFree } from '../../components/FirstSessionFree/FirstSessionFree';
import BenefitLine from '../../components/BenefitLine/BenefitLine';
import { SmartSearchEmpty } from '../SearchPage/NoSearchResultsMaybe/NoSearchResultsMaybe';
import css from './BrowsePage.module.css';

// Browse "Find Your Person" (Sam mockup, Oct 22 2026 6:44 PM). Replaces the earlier search layout.
// Sam Oct 27: price/format filters read the real offer data (offeredFormats, listening rate per 15 min).
const priceOf = l => {
  const rate = Object.values(listeningOfferFromListing(l)?.durations?.['15'] || {}).filter(n => typeof n === 'number');
  if (rate.length) return Math.min(...rate);
  return l?.attributes?.price?.amount != null ? l.attributes.price.amount / 100 : null;
};
const formatsOf = l => {
  const p = l?.attributes?.publicData || {};
  const raw = p.offeredFormats || p.sessionFormats || p.formats || p.channels || p.sessionTypes || [];
  return (Array.isArray(raw) ? raw : [raw]).map(x => String(x).toLowerCase()).map(x => (x === 'chat' ? 'text' : x));
};

// Sam Oct 27: seasonal "hot right now" chips (late Oct). Each matches any of its words in a profile.
const SEASONAL_CHIPS = [
  ['fall-holidays', '🍂 Fall & holidays', ['holiday', 'thanksgiving', 'fall', 'decorat', 'cooking', 'family']],
  ['election', '🗳️ Election stress', ['election', 'politic', 'stress', 'venting', 'news']],
  ['time-change', '🌙 Sleep & time change', ['sleep', 'tired', 'insomnia', 'stress']],
  ['holiday-deals', '🛍️ Holiday deals', ['shopping', 'deals', 'gift', 'budget', 'money']],
];

const BrowsePage = () => {
  const myId = useSelector(s => s?.user?.currentUser?.id?.uuid);
  const { listings, presence, away: awayMap = {} } = useListenerListings();
  const initialMode = typeof window !== 'undefined' && /learn|skill|teaches=/.test(window.location.search) ? 'learn' : 'talk';
  const [mode, setMode] = useState(initialMode);
  const [q, setQ] = useState(() => (typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('keywords') || ''));
  // Sam 12:31 PM Oct 24: top-bar search icon lands here with ?focus=search; focus the one search box.
  const searchInputRef = useRef(null);
  const location = useLocation();
  useEffect(() => {
    if (!/[?&]focus=search\b/.test(location.search || '')) return;
    let tries = 0;
    const id = window.setInterval(() => {
      const el = searchInputRef.current;
      tries += 1;
      if (el) {
        el.focus({ preventScroll: true });
        el.scrollIntoView({ block: 'center' });
      }
      if (el || tries > 40) window.clearInterval(id);
    }, 50);
    return () => window.clearInterval(id);
  }, [location.search, location.key]);
  const [season, setSeason] = useState(null);
  const [chip, setChip] = useState(() => (typeof window === 'undefined' ? 'all' : new URLSearchParams(window.location.search).get('teaches') || 'all'));
  const [sheet, setSheet] = useState(false);
  const [gender, setGender] = useState('any');
  const [city, setCity] = useState('any');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [fmt, setFmt] = useState('any');
  const [maxPrice, setMaxPrice] = useState('');
  const sp = typeof window === 'undefined' ? new URLSearchParams('') : new URLSearchParams(window.location.search);
  // Home "Use your free session now" (?availableNow=only&treat=1): Available now + Sweet Treat only.
  const [treat, setTreat] = useState(sp.get('treat') === '1');
  // Sam Oct 26: dashboard Book a Sweet Spot (?book=later) = schedule-for-later browse.
  const bookLater = sp.get('book') === 'later';
  const [nowOnly, setNowOnly] = useState(sp.get('availableNow') === 'only');
  // Sam 2:53 Oct 23: switching mode, category or search drops the free / Available now filter.
  const clearFree = () => {
    setTreat(false);
    setNowOnly(false);
    if (typeof window !== 'undefined' && /treat=|availableNow=/.test(window.location.search)) {
      const u = new URLSearchParams(window.location.search);
      u.delete('treat');
      u.delete('availableNow');
      const qs = u.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
  };
  // FB bounce fix (Oct 30): theme landings pass ?topics=a,b — rank those listeners first.
  const [themeTopics, setThemeTopics] = useState(() => String(sp.get('topics') || '').split(',').map(t => t.trim()).filter(Boolean));
  const offers = usePublicOffers();
  // Sam 1:01 PM Oct 24: Browse all / See all clears search + every filter.
  const showAll = () => {
    clearFree(); setThemeTopics([]); setMode('talk'); setQ(''); setChip('all'); setSeason(null); setFmt('any'); setMaxPrice(''); setSubs([]); setGender('any'); setCity('any'); setSuggestOpen(false);
  };

  const base = useMemo(
    () =>
      (listings || [])
        .filter(l => !myId || l?.author?.id?.uuid !== myId)
        .map(l => {
          const skills = skillOffersFromListing(l).filter(s => s.id !== LISTENING_OFFER_ID);
          const topics = topicsFromListing(l);
          const a = l.attributes || {};
          const name = l.author?.attributes?.profile?.displayName || '';
          return {
            l,
            skills,
            topics,
            paths: pathsFromListing(l) || [],
            online: !!presence?.[l.id.uuid],
            away: !!awayMap?.[l.id.uuid],
            gender: publicGender(l),
            city: visibleProfileReveals(l).city || '',
            hay: `${name} ${a.title || ''} ${a.description || ''} ${topics.join(' ')} ${skills.map(s => s.label).join(' ')}`,
          };
        }),
    [listings, presence, awayMap, myId]
  );
  const needle = q.trim().toLowerCase();
  // Sam 12:26-12:30 Oct 23: one dropdown that follows the mode. Talk = conversation topics,
  // Learn = skill categories (no topics). Only options that have listings are shown.
  const options = useMemo(() => {
    if (mode === 'learn') {
      const have = new Set(base.flatMap(x => x.skills.map(s => s.id)));
      const cats = SKILL_CATEGORIES.filter(c => have.has(c.id)).map(c => [c.id, c.label]);
      const custom = new Map();
      base.forEach(x => x.skills.forEach(s => { if (!SKILL_CATEGORIES.some(c => c.id === s.id) && s.id !== 'listening') custom.set(s.id, s.label); }));
      return [...cats, ...[...custom.entries()].sort((p1, p2) => String(p1[1]).localeCompare(String(p2[1])))];
    }
    const have = new Set(base.flatMap(x => x.topics).filter(t => t !== 'other'));
    return [...have].map(id => [id, SESSION_TOPIC_LABELS[id] || id]).sort((a, b) => a[1].localeCompare(b[1]));
  }, [base, mode]);
  const cat = options.some(o => o[0] === chip) ? chip : 'all';
  // Sam 2:10 AM: Subtopic chips under Topic (2nd level of the setup taxonomy), only ones people picked.
  const [subs, setSubs] = useState([]);
  const subOptions = useMemo(() => {
    if (mode === 'learn' || cat === 'all') return [];
    const m = new Map();
    // Sam 8:14 AM: always list the topic's own subtopics from the taxonomy.
    const rootPath = LEGACY_TO_PATH[cat] || cat;
    childrenOf(rootPath).filter(Boolean).forEach(n => {
      if (n && n.path && !String(n.id || '').startsWith('~')) m.set(n.path, n.label || pathLabels(n.path).slice(-1)[0] || n.path);
    });
    base.forEach(x => x.paths.forEach(p => {
      const root = LEGACY_TO_PATH[cat];
      const ps = String(p);
      let k = null;
      if (root && ps.startsWith(root + '/')) {
        const seg = ps.slice(root.length + 1).split('/')[0];
        if (seg && !seg.startsWith('~')) k = root + '/' + seg;
      } else if (!root && legacyTopicForPath(p) === cat) {
        const sp = ps.split('/').slice(0, 2);
        if (sp.length === 2 && !sp[1].startsWith('~')) k = sp.join('/');
      }
      if (k && !m.has(k)) m.set(k, pathLabels(k).slice(-1)[0] || k);
    }));
    return [...m.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [base, cat, mode]);
  const subSel = subs.filter(k => subOptions.some(o => o[0] === k));
  const suggestions = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    const hit = ([k, label]) => String(label).toLowerCase().includes(t) || String(k).toLowerCase().includes(t);
    const topicHits = options.filter(hit).map(([k, label]) => [k, label, mode === 'learn' ? 'skill' : 'topic']);
    if (mode === 'learn') return topicHits.slice(0, 6);
    // Sam Oct 27: one search box loads topics AND teachings (Sweet Skillz).
    const skillMap = new Map();
    base.forEach(x => x.skills.forEach(sk => skillMap.has(sk.id) || skillMap.set(sk.id, sk.label)));
    const skillHits = [...skillMap.entries()].filter(hit).map(([k, label]) => [k, label, 'skill']);
    return [...topicHits.slice(0, 4), ...skillHits.slice(0, 3)].slice(0, 7);
  }, [q, options, base, mode]);
  const pickEntry = e => {
    setSuggestOpen(false);
    if (treat || nowOnly) clearFree();
    if (e.kind === 'skill') { setMode('learn'); setQ(''); setChip(e.id); return; }
    setMode('talk');
    if (base.some(x => x.topics.includes(e.id))) { setQ(''); setChip(e.id); } else { setChip('all'); setQ(e.label); }
  };
  const rows = useMemo(() => {
    let r;
    if (mode === 'learn') {
      // Learn lists teaching offers only: one card per skill, titled by the skill.
      // Sam Oct 27: "All skills" = one card per teacher (skills as tags); a picked/searched skill = that skill's card.
      if (cat !== 'all') {
        r = base.flatMap(x => x.skills.filter(s => s.id === cat).map(s => ({ ...x, skill: s, key: `${x.l.id.uuid}:${s.id}` })));
      } else if (needle) {
        const bySkill = base.map(x => { const s = x.skills.find(k => `${k.label} ${k.id}`.toLowerCase().includes(needle)); return s ? { ...x, skill: s, key: `${x.l.id.uuid}:${s.id}` } : null; }).filter(Boolean);
        r = bySkill.length ? bySkill : base.filter(x => x.skills.length && x.hay.toLowerCase().includes(needle)).map(x => ({ ...x, skill: null, key: x.l.id.uuid }));
      } else {
        r = base.filter(x => x.skills.length).map(x => ({ ...x, skill: null, key: x.l.id.uuid }));
      }
    } else {
      r = base.map(x => ({ ...x, key: x.l.id.uuid })).filter(x => cat === 'all' || x.topics.includes(cat));
      if (subSel.length) r = r.filter(x => x.paths.some(p => subSel.some(k => { const a = aliasOf(k); return p === k || String(p).startsWith(k + '/') || (a && (p === a || String(p).startsWith(a + '/'))); })));
      if (needle) r = r.filter(x => x.hay.toLowerCase().includes(needle));
      const sc = season && SEASONAL_CHIPS.find(c => c[0] === season);
      if (sc) r = r.filter(x => { const h = `${x.hay} ${x.paths.join(' ')}`.toLowerCase(); return sc[2].some(w => h.includes(w)); });
    }
    if (nowOnly) r = r.filter(x => x.online);
    if (gender !== 'any') r = r.filter(x => x.gender === gender);
    if (city !== 'any') r = r.filter(x => x.city.toLowerCase() === city.toLowerCase());
    if (treat) r = offers ? r.filter(x => listenerOffersFree(offers, x.l?.author?.id?.uuid)) : [];
    if (fmt !== 'any') r = r.filter(x => !formatsOf(x.l).length || formatsOf(x.l).some(f => f.includes(fmt)));
    if (maxPrice) r = r.filter(x => priceOf(x.l) == null || priceOf(x.l) <= Number(maxPrice));
    const onTheme = x => (themeTopics.length && (x.topics || []).some(t => themeTopics.includes(t)) ? 1 : 0);
    return r.slice().sort((a, b) => Number(b.online) - Number(a.online) || onTheme(b) - onTheme(a));
  }, [base, mode, cat, needle, nowOnly, fmt, maxPrice, treat, offers, gender, city, season, subSel.join("|"), themeTopics.join("|")]);
  const cityOptions = useMemo(() => { const m = {}; base.forEach(x => { if (x.city) m[x.city.toLowerCase()] = m[x.city.toLowerCase()] || x.city; }); return Object.values(m).sort(); }, [base]);
  const onlineRows = rows.filter(r => r.online);
  // FB bounce fix (Oct 30): don't paint "0 people · 0 available now" / empty states while
  // offers + presence are still arriving. Hold a loading state until first results land
  // (or 6s grace, then show the honest empty state).
  const [graceOver, setGraceOver] = useState(false);
  const hasListings = !!listings;
  useEffect(() => {
    if (!hasListings) return undefined;
    const t = setTimeout(() => setGraceOver(true), 6000);
    return () => clearTimeout(t);
  }, [hasListings]);
  const searchReady = hasListings && (!treat || !!offers) && (rows.length > 0 || graceOver || !(treat || nowOnly));
  const laterRows = rows.filter(r => !r.online);
  const topChips = useMemo(() => {
    const n = {};
    base.forEach(x => x.topics.forEach(t => { n[t] = (n[t] || 0) + 1; }));
    return options.filter(([k]) => n[k]).sort((a, b) => n[b[0]] - n[a[0]]).slice(0, 6);
  }, [base, options]);
  const activeFilters = !!season + (fmt !== 'any') + !!maxPrice + nowOnly + (cat !== 'all') + (subSel.length > 0) + (gender !== 'any') + (city !== 'any');

  return (
    <Page title="Find Your Person | Sweet Listeners" scrollingDisabled={false}>
      <LayoutSingleColumn topbar={<TopbarContainer currentPage="SearchPage" />} footer={<FooterContainer />}>
        <div className={css.root} data-page="browse" data-browse-mode={mode}>
          <div className={css.hero}>
            {bookLater ? <h1 className={css.h1} data-browse-booklater="true">Book a <span className={css.pink}>Sweet Spot</span></h1> : <h1 className={css.h1}>Find Your <span className={css.pink}>Person</span></h1>}
            {bookLater ? <p className={css.sub}>Pick someone you like, then choose a time on their calendar that works for you.</p> : null}
            <p className={css.sub} style={bookLater ? { display: 'none' } : undefined} data-browse-ask="true">What would you like to talk about?</p>
            <BenefitLine center text="Text, voice or video, whatever feels easiest." />
          </div>
          {/* Sam Oct 24 late: wholesome photo replaces the Let's Talk / Let's Learn cards (mode lives in Filters). */}
          <img src={browsePhoto} alt="A smiling woman on a friendly video call at home" data-browse-photo="true" className={css.stayPhoto}
            style={{ display: 'block', width: '100%', maxHeight: 130, objectFit: 'cover', borderRadius: 16, margin: '4px 0 12px' }} />
          {/* Sam 7:48 PM Oct 23: one search box. Typing suggests topics/skills; tap = topic filter, Enter = keyword search. */}
          <div className={css.searchWrap} data-browse-onebox={mode} data-browse-box="true">
            <div className={css.searchRow}>
            <label className={css.search}>
              <span aria-hidden="true">🔍</span>
              <input
                type="search"
                value={q}
                enterKeyHint="search"
                onChange={e => { setQ(e.target.value); setSuggestOpen(true); if (treat || nowOnly) clearFree(); }}
                onFocus={() => setSuggestOpen(true)}
                onBlur={() => window.setTimeout(() => setSuggestOpen(false), 150)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); setSuggestOpen(false); e.currentTarget.blur(); } }}
                placeholder={mode === 'learn' ? 'Search skills' : 'Search any topic or interest…'}
                ref={searchInputRef}
                data-browse-search="true"
                role="combobox"
                aria-expanded={suggestOpen && suggestions.length > 0}
                aria-controls="browse-suggest"
                autoComplete="off"
              />
              {q ? (
                <button type="button" className={css.clearQ} onMouseDown={e => e.preventDefault()} onClick={() => { setQ(''); searchInputRef.current && searchInputRef.current.focus(); }} aria-label="Clear search" data-browse-clear="true">✕</button>
              ) : null}
            </label>
            <button type="button" className={css.searchBtn} onClick={() => { setSuggestOpen(false); searchInputRef.current && searchInputRef.current.blur(); }} data-browse-search-btn="true">Search</button>
            <button type="button" className={css.filtersPink} onClick={() => setSheet(true)} data-browse-filters="true">
              Filters{activeFilters ? ` (${activeFilters})` : ''}
            </button>
            </div>
            {suggestOpen && suggestions.length ? (
              <ul className={css.suggest} id="browse-suggest" role="listbox" data-browse-suggest="true">
                {suggestions.map(([k, label, kind]) => (
                  <li key={`${kind}:${k}`} role="option" aria-selected={cat === k}>
                    <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => (kind === 'skill' && mode !== 'learn' ? pickEntry({ kind, id: k, label }) : (setChip(k), setQ(''), setSuggestOpen(false), (treat || nowOnly) && clearFree()))} data-suggest={k} data-suggest-kind={kind}>
                      <span aria-hidden="true">{kind === 'skill' ? '🎓' : '💬'}</span> {kind === 'skill' && mode !== 'learn' ? 'Learn: ' : ''}{label}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {cat !== 'all' ? (
              <div className={css.chipRow}>
                <button type="button" className={css.filterChip} onClick={() => setChip('all')} data-chip="topic">
                  {(options.find(([k]) => k === cat) || [null, cat])[1]} <span aria-hidden="true">✕</span>
                </button>
              </div>
            ) : null}
            {false && listings ? (
              <p style={{ margin: '8px 2px 0', fontSize: 14, fontWeight: 600, color: '#0B2B5B' }} data-browse-count={rows.length}>
                {rows.length} {rows.length === 1 ? 'person' : 'people'}{activeFilters || q ? ' match' : ''}
              </p>
            ) : null}
          </div>
          {/* Search stay fixes (Sam, Oct 26 2026): one free CTA, count + topic chips, Available now first. */}
          {searchReady ? (
            <div className={css.stayBar} data-stay-bar="true">
              {/* Sam Oct 26 9:16 PM: Talk Now — free CTA dropped from Browse (kept on home + dashboard). */}
              <p className={css.stayCount} data-stay-count="true"><b>{rows.length} {rows.length === 1 ? 'person' : 'people'}</b> · <span className={css.stayNow}>{onlineRows.length} online</span></p>
              {/* Sam Oct 26 9:31 PM: topic dropdown lives under Filters only; hot topic chips stay. */}
              {mode === 'talk' && topChips.length ? <p className={css.popularTitle} data-popular-title="true">Browse popular topics</p> : null}
              {mode === 'talk' && topChips.length ? (
                <div className={css.stayChips} role="group" aria-label="Topics" data-stay-topics="true">
                  <button type="button" aria-pressed={cat === 'all'} className={cat === 'all' ? css.stayChipOn : css.stayChip} onClick={() => { setChip('all'); setSeason(null); }} data-topic-chip="all">All</button>
                  {SEASONAL_CHIPS.map(([k, label]) => (
                    <button key={k} type="button" aria-pressed={season === k} className={season === k ? css.stayChipOn : css.stayChip} onClick={() => { setSeason(season === k ? null : k); setChip('all'); if (treat || nowOnly) clearFree(); }} data-season-chip={k}>{label}</button>
                  ))}
                  {topChips.map(([k, label]) => (
                    <button key={k} type="button" aria-pressed={cat === k} className={cat === k ? css.stayChipOn : css.stayChip} onClick={() => { setChip(cat === k ? 'all' : k); setSeason(null); if (treat || nowOnly) clearFree(); }} data-topic-chip={k}>{label}</button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          <div className={css.headRow}>
            <h2 className={css.h2} data-browse-heading="true">{needle || cat !== 'all' ? (mode === 'learn' ? 'Matching teachers' : 'Matching listeners') : mode === 'learn' ? 'People to Learn From' : 'People to talk with or learn from'}</h2>
            <button type="button" className={css.seeAll} onClick={showAll} data-browse-see-all="true">See all</button>
          </div>
          {treat ? (
            <p className={css.treatNote} data-treat-filter="true">
              {nowOnly ? 'Online now with a free first 15 minutes ($0). You only need to be 18+.' : 'People who offer your first 15 minutes free ($0).'}
            </p>
          ) : null}
          {treat && nowOnly && searchReady && offers && !rows.length ? (
            <div className={css.treatEmpty} data-treat-empty="true">
              <b>No one’s free to talk this minute</b>
              <p>Your free 15 minutes are saved. Book a time that suits you, or check back soon.</p>
              <div className={css.treatEmptyBtns}>
                <button type="button" className={css.treatEmptyPrimary} onClick={clearFree} data-treat-empty-all="true">Show everyone</button>
                <button type="button" className={css.treatEmptyGhost} onClick={() => setNowOnly(false)} data-treat-empty-book="true">Book for later</button>
              </div>
            </div>
          ) : null}
          {!searchReady ? <p className={css.muted} data-search-loading="true" aria-live="polite">{nowOnly ? "Finding people online…" : "Finding friendly people…"}</p> : null}
          {searchReady && !rows.length && (treat || nowOnly) && !(treat && nowOnly) ? (
            <div className={css.treatEmpty} data-filter-empty="true"><b>No one matches these filters</b><div className={css.treatEmptyBtns}><button type="button" className={css.treatEmptyPrimary} onClick={clearFree}>Show everyone</button></div></div>
          ) : null}
          {searchReady && !rows.length && !treat && !nowOnly && (needle || cat !== 'all') ? (
            <SmartSearchEmpty query={q.trim() || (options.find(([k]) => k === cat) || [null, cat])[1]} onPick={pickEntry} />
          ) : null}
          {searchReady && !rows.length && !treat && !nowOnly && !(needle || cat !== 'all') ? <p className={css.muted}>No one matches that yet. {mode === 'learn' ? 'Try another skill or clear filters.' : 'Try another topic or clear filters.'}</p> : null}
          {searchReady && rows.length && !onlineRows.length ? (
            <div className={css.treatEmpty} data-stay-empty-now="true">
              <b>No one’s available this minute</b>
              <p>Pick someone below and book a time — your free first 15 minutes are saved.</p>
            </div>
          ) : null}
          {onlineRows.length ? <h3 className={css.staySection} id="browse-now" data-stay-section="now"><span className={css.stayDot} /> Online · {onlineRows.length}</h3> : null}
          <ul className={css.grid} data-browse-grid={rows.length}>
            {onlineRows.map(r => (
              <li key={r.key}>
                <ListenerBrowseCard listing={r.l} online={r.online} away={r.away} skillId={mode === 'learn' && r.skill ? r.skill.id : null} learnTitle={mode === 'learn'} ctaVariant={bookLater || treat || !r.online ? 'book' : 'view'} bookLater={bookLater} />
              </li>
            ))}
          </ul>
          {laterRows.length ? <h3 className={css.staySection} data-stay-section="later">Book for later · {laterRows.length}</h3> : null}
          <ul className={css.grid}>
            {laterRows.map(r => (
              <li key={r.key}>
                <ListenerBrowseCard listing={r.l} online={r.online} away={r.away} skillId={mode === 'learn' && r.skill ? r.skill.id : null} learnTitle={mode === 'learn'} ctaVariant={bookLater || treat || !r.online ? 'book' : 'view'} bookLater={bookLater} />
              </li>
            ))}
          </ul>
          {sheet ? (
            <div className={css.backdrop} onClick={() => setSheet(false)}>
              <div className={css.sheet} role="dialog" aria-label="Filters" onClick={e => e.stopPropagation()} data-browse-sheet="true">
                <div className={css.sheetHead}><b>Filters</b><button type="button" onClick={() => setSheet(false)} aria-label="Close">✕</button></div>
                <p className={css.fLabel}>Name or keyword</p>
                <input type="search" className={css.sheetSelect} value={q} placeholder="Search by name or keyword" onChange={e => { setQ(e.target.value); if (treat || nowOnly) clearFree(); }} aria-label="Search by name or keyword" data-sheet-name="true" />
                <p className={css.fLabel}>Show</p>
                <div className={css.sheetQuick} role="group" aria-label="Quick filters" data-browse-quick="true">
                  {[
                    ['all', 'Browse all', mode === 'talk' && !q && !season && cat === 'all' && gender === 'any' && city === 'any' && !nowOnly && !treat && fmt === 'any' && !maxPrice && !subSel.length, showAll],
                    ['talk', '💬 Talk', mode === 'talk', () => { if (mode !== 'talk') { setMode('talk'); setChip('all'); } }],
                    ['learn', '🎓 Learn', mode === 'learn', () => { if (mode !== 'learn') { setMode('learn'); setChip('all'); } }],
                    ['now', '🟢 Online', nowOnly, () => setNowOnly(!nowOnly)],
                    ['free', '🎁 Free 15 min', treat, () => setTreat(!treat)],
                  ].map(([k, label, on, fn]) => (
                    <button key={k} type="button" aria-pressed={!!on} className={on ? css.qChipOn : css.qChip} onClick={fn} data-quick-chip={k}>{label}</button>
                  ))}
                </div>
                <p className={css.fLabel}>{mode === 'learn' ? 'Skill' : 'Topic'}</p>
                <select className={css.sheetSelect} value={cat} onChange={e => { setChip(e.target.value); if (treat || nowOnly) clearFree(); }} data-browse-category={mode}>
                  <option value="all">{mode === 'learn' ? 'All skills' : 'All topics'}</option>
                  {options.map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
                {subOptions.length ? (
                  <>
                    <p className={css.fLabel}>Subtopic</p>
                    <select className={css.sheetSelect} value={subSel[0] || 'all'} onChange={e => setSubs(e.target.value === 'all' ? [] : [e.target.value])} data-browse-subtopics="true">
                      <option value="all">All subtopics</option>
                      {subOptions.map(([k, label]) => (
                        <option key={k} value={k}>{label}</option>
                      ))}
                    </select>
                  </>
                ) : null}
                {/* Sam Oct 26 9:33 PM: Gender + Location — only listeners who chose Show on profile match. */}
                <p className={css.fLabel}>Gender</p>
                <select className={css.sheetSelect} value={gender} onChange={e => setGender(e.target.value)} data-browse-gender="true">
                  <option value="any">Any</option>
                  <option value="woman">Woman</option>
                  <option value="man">Man</option>
                  <option value="nonbinary">Nonbinary</option>
                </select>
                <p className={css.fLabel}>Location</p>
                <select className={css.sheetSelect} value={city} onChange={e => setCity(e.target.value)} data-browse-location="true">
                  <option value="any">Anywhere</option>
                  {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <p className={css.fLabel}>Format</p>
                <div className={css.seg}>
                  {[['any', 'Any'], ['text', 'Text'], ['voice', 'Voice'], ['video', 'Video']].map(([k, l]) => (
                    <button key={k} type="button" className={fmt === k ? css.segOn : css.segBtn} onClick={() => setFmt(k)}>{l}</button>
                  ))}
                </div>
                <p className={css.fLabel}>Price per 15 min (up to)</p>
                <div className={css.seg} style={{ flexWrap: 'wrap' }}>
                  {/* Sam Oct 25: earners can charge more; Any shows every price. */}
                  {[['', 'Any'], ['5', '$5'], ['10', '$10'], ['20', '$20'], ['30', '$30'], ['40', '$40'], ['50', '$50']].map(([k, l]) => (
                    <button key={l} type="button" className={maxPrice === k ? css.segOn : css.segBtn} onClick={() => setMaxPrice(k)}>{l}</button>
                  ))}
                </div>
                <label className={css.check}><input type="checkbox" checked={nowOnly} onChange={e => setNowOnly(e.target.checked)} /> Online only</label>
                <div className={css.sheetActions}>
                  <button type="button" className={css.clear} onClick={() => { setFmt('any'); setMaxPrice(''); setNowOnly(false); setSubs([]); setGender('any'); setCity('any'); setSeason(null); setChip('all'); setQ(''); }}>Clear</button>
                  <button type="button" className={css.apply} onClick={() => setSheet(false)}>Show {rows.length} {rows.length === 1 ? 'person' : 'people'}</button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default BrowsePage;
